import { loadScript } from "../generalUtils";
import { logger } from "../debug";

// Store references to created elements for cleanup
const persadoElements = new Set<HTMLElement>();

// Helper function to create and track Persado elements
function createPersadoElements() {
  if (typeof document === "undefined") {
    return;
  }

  const elements = [
    {
      type: "link",
      attributes: [
        ["rel", "preconnect"],
        ["href", "https://cdn.persa.do/"],
      ],
    },
    {
      type: "link",
      attributes: [
        ["rel", "preconnect"],
        ["href", "https://rts.persado.com/"],
      ],
    },
    {
      type: "link",
      attributes: [
        ["rel", "preload"],
        ["href", "https://cdn.persa.do/webscripts/1459/p.js"],
        ["as", "script"],
      ],
    },
  ];

  elements.forEach(({ type, attributes }) => {
    const htmlElement = document.createElement(type);
    attributes.forEach(([key, value]) => {
      htmlElement.setAttribute(key, value);
    });
    document.head.appendChild(htmlElement);
    persadoElements.add(htmlElement);
  });
}

// Helper function to remove all Persado elements
function removePersadoElements() {
  persadoElements.forEach((element) => {
    if (element.parentNode) {
      element.parentNode.removeChild(element);
    }
  });
  persadoElements.clear();
}

export default (config: {
  name: string;
  enabled?: boolean;
  debug?: boolean;
  isAllowed?: () => boolean;
}) => {
  let scriptLoading: ReturnType<typeof loadScript>;
  let isDisabled = true;
  const { enabled, name, debug } = config;

  const loadPersadoScript = () => {
    if (scriptLoading !== undefined) {
      return scriptLoading;
    }

    if (debug) {
      logger.debugWithScope(
        "consent",
        "[Persado] Loading Persado script from CDN and creating preconnect elements"
      );
    }

    createPersadoElements();
    scriptLoading = loadScript("https://cdn.persa.do/webscripts/1459/p.js");
    return scriptLoading;
  };

  const plugin = {
    name,
    config,
    enabled: enabled ?? false,
    initialize() {
      if (config.isAllowed && !config.isAllowed()) {
        return;
      }
    },
    loaded() {
      if (config.isAllowed && !config.isAllowed()) {
        return true;
      }

      if (isDisabled) {
        return true;
      }

      if (scriptLoading === undefined) {
        return false;
      }

      return scriptLoading.isReady();
    },
    ready() {},
    enablePlugin() {
      if (config.isAllowed && !config.isAllowed()) {
        return;
      }

      if (!isDisabled) {
        return;
      }

      if (debug) {
        logger.debugWithScope(
          "consent",
          "[Persado] Enabling plugin and loading script"
        );
      }
      isDisabled = false;
      loadPersadoScript();
    },
    disablePlugin() {
      if (isDisabled) {
        return;
      }

      if (debug) {
        logger.debugWithScope(
          "consent",
          "[Persado] Disabling plugin and removing all Persado elements"
        );
      }
      isDisabled = true;

      // Remove all elements we created
      removePersadoElements();

      // Also remove any scripts that Persado itself added
      if (typeof document !== "undefined") {
        // Remove all Persado scripts (including ones added by their loader)
        const allPersadoScripts = document.querySelectorAll(
          'script[src*="persado"], script[src*="persa.do"]'
        );
        allPersadoScripts.forEach((script) => {
          if (script.parentNode) {
            script.parentNode.removeChild(script);
          }
        });

        // Remove any Persado-added link elements
        const allPersadoLinks = document.querySelectorAll(
          'link[href*="persado"], link[href*="persa.do"]'
        );
        allPersadoLinks.forEach((link) => {
          if (link.parentNode) {
            link.parentNode.removeChild(link);
          }
        });
      }

      // Clear any Persado-created global variables or DOM modifications
      if (typeof window !== "undefined") {
        // Clean up any Persado global objects
        if (window["persado"]) {
          delete window["persado"];
        }
        if (window["Persado"]) {
          delete window["Persado"];
        }
        if (window["PersadoWS"]) {
          delete window["PersadoWS"];
        }
        if (window["PersadoLib"]) {
          delete window["PersadoLib"];
        }
        if (window["PersadoCode"]) {
          delete window["PersadoCode"];
        }

        // Remove Persado data from localStorage
        try {
          localStorage.removeItem("PersadObj");
        } catch (e) {
          // localStorage might not be available (private browsing, etc.)
        }
      }

      // Reset state so it can be loaded again
      scriptLoading = undefined;
    },
  };

  return plugin;
};
